/**
 * AdminAccountMenu — Avatar dropdown shown in the admin home hero header
 * and any other admin surface that needs a "where is logout?" answer.
 *
 * Renders:
 *   - circular profile photo (or initial fallback)
 *   - dropdown with My Profile · Account Settings · Logout
 *
 * Iter-233 (post-deploy admin feedback): admins should never have to
 * search menus to sign out. Visible on desktop AND mobile.
 * Iter-246b: switched to Shadcn Popover so the menu auto-flips when
 * it would clip past the mobile viewport edge (Mel reported the menu
 * extending off-screen on 390px-wide devices).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { UserCircle, Settings, LogOut, ChevronDown } from 'lucide-react';

const AdminAccountMenu = ({ displayName, profilePhotoUrl, compact = false }) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initial = (displayName || user?.email || 'A').trim().charAt(0).toUpperCase();
  const photoSrc = profilePhotoUrl
    ? (profilePhotoUrl.startsWith('http') ? profilePhotoUrl : `${process.env.REACT_APP_BACKEND_URL}${profilePhotoUrl}`)
    : null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-2 px-2 py-1.5 rounded-full border border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/40 transition-colors"
          data-testid="admin-account-trigger"
          aria-haspopup="menu"
        >
          {photoSrc ? (
            <img src={photoSrc} alt={displayName || 'admin'} className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <span className="w-8 h-8 rounded-full bg-cyan-500 text-white text-sm font-bold grid place-items-center">{initial}</span>
          )}
          {!compact && (
            <span className="hidden md:inline text-sm font-semibold text-slate-700 max-w-[120px] truncate">
              {displayName || 'Admin'}
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        role="menu"
        align="end"
        sideOffset={8}
        collisionPadding={8}
        className="w-56 max-w-[calc(100vw-1rem)] bg-white border border-slate-200 rounded-2xl shadow-lg p-1.5 z-50"
        data-testid="admin-account-dropdown"
      >
        <div className="px-3 py-2 border-b border-slate-100">
          <div className="text-sm font-bold text-slate-900 truncate">{displayName || 'Admin'}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        </div>
        <button
          onClick={() => navigate('/admin/profile')}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg"
          data-testid="admin-menu-profile"
        >
          <UserCircle className="w-4 h-4 text-cyan-600" /> My Profile
        </button>
        <button
          onClick={() => navigate('/admin/settings')}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg"
          data-testid="admin-menu-settings"
        >
          <Settings className="w-4 h-4 text-slate-500" /> Account Settings
        </button>
        <div className="h-px bg-slate-100 my-1" />
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 rounded-lg font-semibold"
          data-testid="admin-menu-logout"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </PopoverContent>
    </Popover>
  );
};

export default AdminAccountMenu;
